/**
 * GapCheckPanel — deflection range gap clearance analysis.
 *
 * Sweeps deflection angles via POST /geometry/cs-gap-check and displays
 * a color-coded results table showing min gap at each angle.
 */

import { useCallback, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Spinner,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import type { Assembly, CSPreviewParams, GapCheckResult, GapCheckResultItem } from "../api/geometry";
import * as geometryApi from "../api/geometry";

interface GapCheckPanelProps {
  assembly: Assembly;
  surfaceName: string;
  csParams: CSPreviewParams;
}

function gapColor(gap_mm: number | null): "red" | "yellow" | "green" | "gray" {
  if (gap_mm === null) return "gray";
  if (gap_mm < 0) return "red";
  if (gap_mm < 2) return "yellow";
  return "green";
}

export function GapCheckPanel({ assembly, surfaceName, csParams }: GapCheckPanelProps) {
  const [deltaMin, setDeltaMin] = useState("-25");
  const [deltaMax, setDeltaMax] = useState("25");
  const [deltaStep, setDeltaStep] = useState("1");
  const [result, setResult] = useState<GapCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCompute = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await geometryApi.csGapCheck({
        assembly_id: assembly.id,
        surface_name: surfaceName,
        eta1: csParams.eta1,
        eta2: csParams.eta2,
        xc1: csParams.xc1,
        xc2: csParams.xc2,
        yh1: csParams.yh1,
        yh2: csParams.yh2,
        gap_mm: csParams.gap_mm,
        delta_min: parseFloat(deltaMin) || -25,
        delta_max: parseFloat(deltaMax) || 25,
        delta_step: parseFloat(deltaStep) || 1,
        section_eta: csParams.section_eta,
        fillet_mm: csParams.fillet_mm,
      });
      setResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [assembly.id, surfaceName, csParams, deltaMin, deltaMax, deltaStep]);

  return (
    <Card size="2">
      <Text size="2" weight="bold" mb="2" style={{ display: "block" }}>
        Gap Clearance Check
      </Text>

      <Flex gap="3" align="end" mb="3">
        <Box>
          <Text size="1" color="gray" mb="1" style={{ display: "block" }}>\u03B4 min (\u00B0)</Text>
          <TextField.Root
            size="1"
            style={{ width: 70 }}
            value={deltaMin}
            onChange={(e) => setDeltaMin(e.target.value)}
          />
        </Box>
        <Box>
          <Text size="1" color="gray" mb="1" style={{ display: "block" }}>\u03B4 max (\u00B0)</Text>
          <TextField.Root
            size="1"
            style={{ width: 70 }}
            value={deltaMax}
            onChange={(e) => setDeltaMax(e.target.value)}
          />
        </Box>
        <Box>
          <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Step (\u00B0)</Text>
          <TextField.Root
            size="1"
            style={{ width: 60 }}
            value={deltaStep}
            onChange={(e) => setDeltaStep(e.target.value)}
          />
        </Box>
        <Button size="2" onClick={handleCompute} disabled={loading}>
          {loading ? <Spinner size="1" /> : "Compute"}
        </Button>
      </Flex>

      {error && (
        <Card size="1" mb="2" style={{ background: "var(--red-2)" }}>
          <Text size="2" color="red">{error}</Text>
        </Card>
      )}

      {result?.has_interference && (
        <Card size="1" mb="2" style={{ background: "var(--red-2)" }}>
          <Text size="2" color="red" weight="bold">
            Interference detected at one or more deflection angles.
          </Text>
        </Card>
      )}

      {result && (
        <Box style={{ maxHeight: 300, overflowY: "auto" }}>
          <Table.Root size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>\u03B4 (\u00B0)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Min Gap (mm)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Location</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {result.results.map((r: GapCheckResultItem) => (
                <Table.Row key={r.delta_deg}>
                  <Table.Cell>
                    <Text size="1" style={{ fontFamily: "monospace" }}>
                      {r.delta_deg.toFixed(1)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge size="1" color={gapColor(r.min_gap_mm)} variant="soft">
                      {r.min_gap_mm === null ? "error" : r.min_gap_mm.toFixed(2)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" color="gray">{r.location}</Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Card>
  );
}

export default GapCheckPanel;
